import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '@core/services/authentication/auth.service';
import { AppState } from '@core/store/app.reducer';
import { Store } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as ui from './core/store/ui/ui.actions';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { LocalStorageService } from '@core/services/local-storage.service';
import { OrderChangeService } from '@core/services/orders/orderChange.service';
import { environment } from '@env/environment';
import { OrderFlowService } from '@core/services/order-flow-service/order-flow-service.service';
import { GtmEventService } from '@core/analytics/gtm-events.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { BrazeService } from '@core/services/braze/braze.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading: Boolean = false;
  uiSubscription!: Subscription;
  readonly isLoggedIn = false;
  hiddenHeader: boolean = false;
  hiddenHeaderSeller: boolean = false;
  user: any;
  readonly appVersion = environment.appVersion;
  activeNotifications: any[] = [];
  private readonly shownNotificationIds: Set<string> = new Set();
  currentRoute: string = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<AppState>,
    public readonly _authService: AuthService,
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService,
    private readonly gtmService: GoogleTagManagerService,
    private readonly orderFlowService: OrderFlowService,
    private readonly orderChangeService: OrderChangeService,
    private readonly cdr: ChangeDetectorRef,
    private readonly _gtmEventService: GtmEventService,
    private readonly _notificationsService: NotificationsService,
    private readonly brazeService: BrazeService
  ) {
    this.gtmService.addGtmToDom();

    router.events.subscribe((event: any) => {
      this.user = this.localStorageService.getItem('user');
      if (event.url) {
        // Actualizar la ruta actual
        this.currentRoute = event.url;

        // Verificar estado de autenticación
        const isAuthenticated = this._authService.isLoggedIn() && this.user;
        const isLoginRoute = event.url === '/login' || event.url === '/';

        // Si está en login o no hay usuario autenticado, limpiar notificaciones
        if (isLoginRoute || !isAuthenticated) {
          this.activeNotifications = [];
          this.shownNotificationIds.clear();
        }

        // Verificar si el modal está activo
        if (this.orderChangeService.getButtonState()) {
          return; // Si el modal está activo, no cambiar la visibilidad del header
        }
        //ocultar rutas para nuevo header
        this.hiddenHeader =
          event.url == '/login' ||
            event.url == '/' ||
            event.url == '/unidades-de-negocio' ||
            event.url == '/error' ||
            event.url == '/404' ||
            event.url == '/500' ||
            event.url == '/503' ||
            event.url == '/504' ||
            event.url == '/seleccionar-cliente' ||
            event.url == '/main' ||
            event.url == '/main/inicio' ||
            event.url == '/main/cliente-seleccionado' ||
            event.url == '/detalle-rutinas' ||
            event.url == '/detalle-bitácora' ||
            event.url == '/main/historial-compras' ||
            event.url.includes('/main/historial-compras/detalle-historial') ||
            event.url == '/main/cuenta-corriente' ||
            event.url == '/main/subir-pedido' ||
            event.url == '/main/avances-ventas' ||
            event.url == '/main/reconocimientosdetalle' ||
            event.url == '/main/mis-reclamos' ||
            event.url == '/main/aliados' ||
            event.url == '/main/ali-socios' ||
            event.url == 'main/pedido-confirmado' ||
            event.url == 'main/error-de-pedido' ||
            event.url.startsWith('/main/los-infaltables') ||
            event.url == '/main/confirmacion' ||
            event.url.includes('/main/checkout')
            ? false
            : true;
        //Mostrar rutas para nuevo header
        this.hiddenHeaderSeller =
          (event.url == '/main' ||
            event.url == '/main/inicio' ||
            event.url == '/main/cliente-seleccionado' ||
            event.url == '/main/historial-compras' ||
            event.url == '/main/cuenta-corriente' ||
            event.url.includes('/main/historial-compras/detalle-historial') ||
            event.url == '/main/subir-pedido' ||
            event.url == '/main/avances-ventas' ||
            event.url == '/main/reconocimientosdetalle' ||
            event.url == '/main/mis-reclamos' ||
            event.url == '/main/aliados' ||
            event.url == '/main/aliados/cierre' ||
            event.url == '/main/aliados/avance' ||
            event.url == '/main/confirmacion' ||
            event.url == '/main/ali-socios' ||
            event.url == '/main/pedido-confirmado' ||
            event.url == '/main/error-de-pedido' ||
            // La vista lleva la pestaña en la URL (?tab=)
            event.url.startsWith('/main/los-infaltables') ||
            event.url.includes('/main/checkout') ||
            event.url.includes('/tienda/busqueda')) &&
            this.user.type === 'Vendedor'
            ? true
            : false;

        // Braze: en /main y /tienda (con cliente seleccionado) = identidad cliente; al salir de ambas = vendedor
        if (event instanceof NavigationEnd && this.user?.type === 'Vendedor') {
          const nav = event as NavigationEnd;
          const urlPath = (nav.urlAfterRedirects ?? nav.url).split('?')[0];
          const isInMainRoutes = urlPath.startsWith('/main');
          const isInTiendaRoutes = urlPath.startsWith('/tienda');
          const clientSelect = this.localStorageService.getItem('clientSelect');
          const hasSelectedClient = !!(clientSelect?.code && clientSelect?.sector);
          const isClientFacingRoute = isInMainRoutes || isInTiendaRoutes;

          if (isClientFacingRoute && hasSelectedClient) {
            this.brazeService.identifyUser(clientSelect.code, {
              email: clientSelect.email ?? '',
              name: clientSelect.name ?? '',
            });
          } else if (!isClientFacingRoute && this.user?.email) {
            this.brazeService.identifyUser(this.user.email, {
              email: this.user.email,
              name: this.user.fullName ?? '',
            });
          }
        }
      }
    });
  }

  ngOnInit(): void {
    this.initStore();
    this.manageSesion();

    // Limpiar storage GTM antiguo al iniciar
    this.cleanOldGtmStorage();

    // Carga inicial de notificaciones de pedidos para el snackbar
    if (this._authService.isLoggedIn()) {
      this.loadOrderNotifications();
    }
  
    // Actualizar snackbar cuando llega un push de Braze (ya refresca notifications-by-token)
    this._notificationsService.onPushRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        const orderNotifications = this.filterOrderNotifications(notifications);
        this.processNotifications(orderNotifications);
      });
  }

  initStore() {
    this.uiSubscription = this.store
      .select('ui')
      .subscribe((ui) => (this.isLoading = ui.isLoading));
    this.checkVersionAndRedirect();
  }

  activeLoading() {
    this.store.dispatch(ui.isLoading());
  }

  deactiveLoading() {
    this.store.dispatch(ui.stopLoading());
  }

  manageSesion(): void {
    if (window.addEventListener) {
      window.addEventListener('storage', (e) => {
        if (e.key == 'alicorp-user')
          if (!this._authService.isLoggedIn()) {
            this._authService.logout();
          }
      });
    }
  }

  checkVersionAndRedirect() {

    const storedVersion = localStorage.getItem('appVersion');

    if (storedVersion !== this.appVersion) {
      // Limpia el localStorage
      localStorage.clear(); // o tu método específico

      // Actualiza la versión
      localStorage.setItem('appVersion', this.appVersion);

      // Redirige al login y fuerza recarga completa de la app
      this.router.navigate(['/login']).then(() => {
        window.location.reload();
      });
    }
  }
  /** Carga notificaciones de pedidos desde notifications-by-token y actualiza el snackbar */
private loadOrderNotifications(): void {
  const params = this._notificationsService.getNotificationParams();

  if (!params?.sector) {
    return;
  }
  this._notificationsService.getNotificationsByToken(params).subscribe({
    next: (res: any) => {
      const list: any[] = Array.isArray(res) ? res : (res?.notifications ?? []);

      // 💡 GUARDAR CACHE EN SERVICE
      this._notificationsService['lastNotifications'] = list;

      const orderNotifications = this.filterOrderNotifications(list);
      this.processNotifications(orderNotifications);
    },
    error: () => { },
  });
}
  
  /** Filtra solo las notificaciones de pedidos que deben mostrarse como Pop Up */
  private filterOrderNotifications(notifications: any[]): any[] {
    return (notifications || []).filter(
      (n: any) => n?.notificationType === 'Pop Up y Campana'
    );
  }

  private processNotifications(notifications: any[]): void {
    // Solo mostrar notificaciones no leídas (isNotified === false)
    const validNotifications = (notifications || []).filter(
      (n: any) => n?.id && !n.isHidden
    );

    const currentIds = this.activeNotifications.map((n: any) => n.id).join(',');
    const newIds = validNotifications.map((n: any) => n.id).join(',');

    if (currentIds !== newIds) {
      const previousIds = new Set(this.activeNotifications.map((n: any) => n.id));
      const newNotifications = validNotifications.filter(
        (n: any) => !previousIds.has(n.id)
      );

      this.triggerGtmEventsForNotifications(newNotifications);

      this.activeNotifications = validNotifications;

      this.shownNotificationIds.clear();
      validNotifications.forEach((n: any) => {
        if (n.id) this.shownNotificationIds.add(n.id);
      });

      this.cdr.detectChanges();
    }
  }

  private async triggerGtmEventsForNotifications(notifications: any[]): Promise<void> {
    for (const notification of notifications) {
      // Extraer orderId de la URL de la notificación (formato: ?orderId=<uuid>)
      const orderId = this.extractOrderIdFromNotificationUrl(notification?.url);

      if (!orderId) {
        continue;
      }

      const isRejected = this.isNotificationRejected(notification);
      const isCreated = !isRejected;

      // Clave de deduplicación
      const processedKey = isCreated ? `CREATED_${String(orderId)}` : `REJECTED_${String(orderId)}`;

      // Verificar si ya se dispararon eventos para este pedido
      const gtmProcessedOrders = this.localStorageService.getItem('gtmProcessedOrders') || {};
      const alreadyProcessed = gtmProcessedOrders[processedKey];

      if (alreadyProcessed) {
        const minutesAgo = (Date.now() - (alreadyProcessed.timestamp || 0)) / (1000 * 60);
        if (minutesAgo <= 5) {
          continue;
        }
        // Más de 5 min: permitir reprocesar solo si aún existen datos en storage
        const gtmOrdersDataCheck = this.localStorageService.getItem('gtmOrdersData') || {};
        if (!gtmOrdersDataCheck[orderId]) {
          continue;
        }
        delete gtmProcessedOrders[processedKey];
        this.localStorageService.setItem('gtmProcessedOrders', gtmProcessedOrders);
      }

      // Obtener eventos guardados del storage usando orderId (internalOrderCode)
      const gtmOrdersData = this.localStorageService.getItem('gtmOrdersData') || {};
      const orderData = gtmOrdersData[orderId];

      if (!orderData || !orderData.gtmEvents) {
        continue;
      }

      const gtmEventsData = orderData.gtmEvents;
      const orderCreator = orderData.orderCreator;
      const user = this.localStorageService.getItem('user');
      const userCode = user?.code;

      const orderCreatorIsEmpty = !orderCreator || Object.keys(orderCreator).length === 0;

      if (!orderCreatorIsEmpty && (!orderCreator.userCode || orderCreator.userCode !== userCode)) {
        continue;
      }

      try {
        if (isCreated) {
          // transaction_id: preferir código externo/SAP del detalle; si no existe aún (timing), usar el mismo internalOrderCode que en gtmOrdersData
          let externalOrderNumber: string | null = null;
          try {
            const detail: any = await firstValueFrom(
              this.orderFlowService.getOrderDetail(orderId)
            );
            externalOrderNumber = detail?.externalOrderCode || detail?.sapOrderNumber || null;
          } catch {
            // Detalle puede fallar o no traer SAP todavía — purchase + purchaseIntention siguen con internalOrderCode
          }

          const extTrim =
            typeof externalOrderNumber === 'string'
              ? externalOrderNumber.trim()
              : '';
          const transactionId = extTrim.length > 0 ? extTrim : orderId;

          await this._gtmEventService.purchaseCarEvent(
            transactionId,
            gtmEventsData.products,
            gtmEventsData.priceFields
          );

          const { changeProducts, replacedProducts, addProducts, deletedProducts } = gtmEventsData;

          for (const product of changeProducts || []) {
            await this._gtmEventService.purchaseIntention(transactionId, "Modifico cantidades", product);
          }

          for (const product of replacedProducts || []) {
            if (product.skuParent) {
              if (product.suggestedProducts && Array.isArray(product.suggestedProducts)) {
                let specificProduct = product.suggestedProducts.find(
                  (suggestedProduct: any) => suggestedProduct.sku === product.skuParent
                );
                if (specificProduct) {
                  let modifiableProduct = { ...specificProduct };
                  modifiableProduct.textoProducto = this.formatStockTextForNotification(modifiableProduct);
                  if (product.add) modifiableProduct.add = product.add;
                  else if (product.replace) modifiableProduct.replace = product.replace;
                  await this._gtmEventService.purchaseIntention(
                    transactionId, "Acepto producto sugerido - reemplazo el producto", modifiableProduct
                  );
                }
              }
            } else {
              await this._gtmEventService.purchaseIntention(
                transactionId, "Acepto producto sugerido - reemplazo el producto", product
              );
            }
          }

          for (const product of addProducts || []) {
            if (product.skuParent) {
              if (product.suggestedProducts && Array.isArray(product.suggestedProducts)) {
                let specificProduct = product.suggestedProducts.find(
                  (suggestedProduct: any) => suggestedProduct.sku === product.skuParent
                );
                if (specificProduct) {
                  let modifiableProduct = { ...specificProduct };
                  modifiableProduct.textoProducto = this.formatStockTextForNotification(modifiableProduct);
                  if (product.add) modifiableProduct.add = product.add;
                  else if (product.replace) modifiableProduct.replace = product.replace;
                  await this._gtmEventService.purchaseIntention(
                    transactionId, "Acepto producto sugerido - añadió producto", modifiableProduct
                  );
                }
              }
            } else {
              await this._gtmEventService.purchaseIntention(
                transactionId, "Acepto producto sugerido - añadió producto", product
              );
            }
          }

          for (const product of deletedProducts || []) {
            if (product.textoProducto !== 'Con Stock') {
              await this._gtmEventService.purchaseIntention(transactionId, "Elimino el producto", product);
            }
          }

          const updatedProcessed = this.localStorageService.getItem('gtmProcessedOrders') || {};
          updatedProcessed[processedKey] = {
            processed: true,
            timestamp: Date.now(),
            purchaseCarEventDispatched: true,
            purchaseIntentionEventsCount:
              (changeProducts?.length || 0) +
              (replacedProducts?.length || 0) +
              (addProducts?.length || 0) +
              (deletedProducts?.length || 0),
          };
          this.localStorageService.setItem('gtmProcessedOrders', updatedProcessed);

          setTimeout(() => {
            const data = this.localStorageService.getItem('gtmOrdersData') || {};
            if (data[orderId]) {
              delete data[orderId];
              this.localStorageService.setItem('gtmOrdersData', data);
            }
          }, 60000);

        } else {
          // REJECTED
          const combinedItems = this._gtmEventService.combineItemsWithPriceFields(
            gtmEventsData.products || [],
            gtmEventsData.priceFields || []
          );

          const firstItem = combinedItems?.[0];
          const value = Array.isArray(gtmEventsData.priceFields)
            ? gtmEventsData.priceFields.reduce(
              (sum: number, row: any) => sum + (Number(row?.totalPrice) || 0),
              0
            )
            : 0;

          const error_message = notification?.message || 'Pedido rechazado';

          await this._gtmEventService.purchaseFailed({
            value: Number(value) || 0,
            item: {
              item_name: firstItem?.item_name || '',
              item_id: firstItem?.item_id || '',
              item_brand: firstItem?.item_brand || '',
              item_category: firstItem?.item_category || '',
              quantity: Number(firstItem?.quantity) || 0,
              price: Number(firstItem?.price) || 0,
              totalPrice: Number(firstItem?.totalPrice) || 0,
            },
            error_type: 'order_rejected',
            error_code: 'REJECTED',
            error_message: String(error_message),
          });

          const updatedProcessed = this.localStorageService.getItem('gtmProcessedOrders') || {};
          updatedProcessed[processedKey] = {
            processed: true,
            timestamp: Date.now(),
            purchaseFailedDispatched: true,
          };
          this.localStorageService.setItem('gtmProcessedOrders', updatedProcessed);

          setTimeout(() => {
            const data = this.localStorageService.getItem('gtmOrdersData') || {};
            if (data[orderId]) {
              delete data[orderId];
              this.localStorageService.setItem('gtmOrdersData', data);
            }
          }, 60000);
        }
      } catch (error) {
        console.error(`Error al disparar eventos GTM para notificación`, error);
      }
    }
  }

  /** Determina si una notificación es de pedido rechazado según el título */
  private isNotificationRejected(notification: any): boolean {
    const title: string = notification?.title ?? '';
    return title.toLowerCase().includes('no logramos');
  }

  /** Extrae el orderId (UUID) del campo url de la notificación */
  private extractOrderIdFromNotificationUrl(url: string): string | null {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('orderId');
    } catch {
      const match = url.match(/[?&]orderId=([^&]+)/);
      return match ? match[1] : null;
    }
  }

  private formatStockTextForNotification(product: any): string {
    let textoProducto = 'Con Stock';
    if (product.stockReal <= 0) {
      textoProducto = 'Sin Stock';
    } else if (product.stockReal > 0 && product.count) {
      const count = Number.parseInt(String(product.count).replace(/[^0-9-.]/g, ''), 10);
      if (product.stockReal < count) {
        textoProducto = 'Sin Suficiente stock';
      }
    }
    return textoProducto;
  }

  readonly handleVisibilityChange = () => { };

  onNotificationClosed(notificationId: string): void {
    this.activeNotifications = this.activeNotifications.filter(
      (n: any) => n.id !== notificationId
    );
    this.shownNotificationIds.delete(notificationId);
    this.cdr.detectChanges();
  }

  private cleanOldGtmStorage(): void {
    // Limpiar storage de eventos GTM antiguos (más de 7 días)
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

    try {
      // Limpiar gtmOrdersData antiguos
      const gtmOrdersData = this.localStorageService.getItem('gtmOrdersData') || {};
      let hasChanges = false;

      for (const orderId in gtmOrdersData) {
        const orderData = gtmOrdersData[orderId];
        const timestamp = orderData?.orderCreator?.timestamp || orderData?.gtmEvents?.timestamp || 0;
        if (timestamp < sevenDaysAgo) {
          delete gtmOrdersData[orderId];
          hasChanges = true;
        }
      }

      if (hasChanges) {
        this.localStorageService.setItem('gtmOrdersData', gtmOrdersData);
      }

      // Limpiar flags gtmProcessedOrders antiguos
      const gtmProcessedOrders = this.localStorageService.getItem('gtmProcessedOrders') || {};
      let hasProcessedChanges = false;

      for (const sapOrderNumber in gtmProcessedOrders) {
        const processedData = gtmProcessedOrders[sapOrderNumber];
        if (processedData && processedData.timestamp && processedData.timestamp < sevenDaysAgo) {
          delete gtmProcessedOrders[sapOrderNumber];
          hasProcessedChanges = true;
        }
      }

      if (hasProcessedChanges) {
        this.localStorageService.setItem('gtmProcessedOrders', gtmProcessedOrders);
      }
    } catch (error) {
      console.error('Error al limpiar storage GTM antiguo:', error);
    }
  }

  ngOnDestroy(): void {
    this.uiSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}
